import {
  Alert,
  Badge,
  Button,
  Field,
  Modal,
  PageHeader,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Percent, Plus } from 'lucide-react';
import { type JSX, useState } from 'react';

import { ApiError } from '../../api/client';
import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import {
  createWithholdingType,
  listWithholdingTypes,
  updateWithholdingType,
  type WithholdingType,
} from '../../payments/withholding-types-api';

type ModalState = { mode: 'create' } | { mode: 'edit'; type: WithholdingType } | null;

const RATE_ERROR = 'Ingresá un valor mayor a 0 y hasta 100, con hasta dos decimales.';

/** Mirrors `isValidDefaultRatePercent` (`apps/api` `withholding-type.rules.ts`). */
function parseRate(raw: string): { value: number | null; valid: boolean } {
  const trimmed = raw.trim();

  if (trimmed === '') {
    return { value: null, valid: true };
  }

  if (!/^\d+([.,]\d{1,2})?$/.test(trimmed)) {
    return { value: null, valid: false };
  }

  const value = Number(trimmed.replace(',', '.'));

  return value > 0 && value <= 100 ? { value, valid: true } : { value: null, valid: false };
}

function formatRate(rate: number | null): string {
  return rate === null ? '—' : `${String(rate).replace('.', ',')} %`;
}

/** Codes from `PaymentExceptionFilter` (`apps/api` `payment-receipts.errors.filter.ts`). */
function mapWithholdingTypeError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 409 && code === 'WITHHOLDING_TYPE_NAME_DUPLICATE') {
    return 'Ya existe un tipo de retención con ese nombre.';
  }

  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  if (error.status === 404) {
    return 'El tipo de retención ya no existe. Actualizá la página.';
  }

  if (error.status === 422) {
    return 'Revisá los datos ingresados e intentá de nuevo.';
  }

  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}

/**
 * `withholding-types/spec.md` — "Pantalla de configuración de tipos de
 * retención". The hub card is gated on `settings.manage`; the page
 * double-checks it because a direct URL must not render the table.
 */
export function WithholdingTypeConfigPage(): JSX.Element {
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [modal, setModal] = useState<ModalState>(null);
  const [name, setName] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [rate, setRate] = useState('');

  const canManage = hasPermission(currentUser.data, 'settings.manage');
  const query = useQuery({
    queryKey: ['withholding-types'],
    queryFn: listWithholdingTypes,
    enabled: canManage,
  });

  const parsedRate = parseRate(rate);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (modal === null || !parsedRate.valid) {
        return;
      }

      const trimmedJurisdiction = jurisdiction.trim();
      const input = {
        name: name.trim(),
        jurisdiction: trimmedJurisdiction === '' ? null : trimmedJurisdiction,
        defaultRatePercent: parsedRate.value,
      };

      if (modal.mode === 'create') {
        await createWithholdingType(input);
      } else {
        await updateWithholdingType(modal.type.id, input);
      }
    },
    onSuccess: async () => {
      toast('success', 'Guardado');
      setModal(null);
      await queryClient.invalidateQueries({ queryKey: ['withholding-types'] });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updateWithholdingType(id, { isActive }),
    onSuccess: async () => {
      toast('success', 'Guardado');
      await queryClient.invalidateQueries({ queryKey: ['withholding-types'] });
    },
  });

  function openModal(state: ModalState): void {
    saveMutation.reset();
    setName(state?.mode === 'edit' ? state.type.name : '');
    setJurisdiction(state?.mode === 'edit' ? (state.type.jurisdiction ?? '') : '');
    setRate(
      state?.mode === 'edit' && state.type.defaultRatePercent !== null
        ? String(state.type.defaultRatePercent)
        : '',
    );
    setModal(state);
  }

  if (currentUser.isPending) {
    return <Skeleton className="h-40 w-full" />;
  }

  if (!canManage) {
    return (
      <Alert tone="danger" title="Sección restringida">
        No tenés permiso para acceder a los tipos de retención.
      </Alert>
    );
  }

  const types = query.data ?? [];

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Percent}
        title="Tipos de retención"
        description="Definí las retenciones que se pueden registrar en una cobranza y su alícuota sugerida."
        actions={
          <Button icon={Plus} onClick={() => openModal({ mode: 'create' })}>
            Nuevo tipo de retención
          </Button>
        }
      />

      {query.isError ? (
        <Alert tone="danger" title="No pudimos cargar los tipos de retención">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {toggleActiveMutation.isError ? (
        <Alert tone="danger">{mapWithholdingTypeError(toggleActiveMutation.error)}</Alert>
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {query.data !== undefined ? (
        <Table>
          <caption className="sr-only">Tipos de retención</caption>
          <thead>
            <tr>
              <TableHeader>Nombre</TableHeader>
              <TableHeader>Jurisdicción</TableHeader>
              <TableHeader>Alícuota por defecto</TableHeader>
              <TableHeader>Líneas que lo usan</TableHeader>
              <TableHeader>Estado</TableHeader>
              <TableHeader>Acciones</TableHeader>
            </tr>
          </thead>
          <TableBody>
            {types.map((type) => (
              <TableRow key={type.id}>
                <TableCell>{type.name}</TableCell>
                <TableCell>{type.jurisdiction ?? '—'}</TableCell>
                <TableCell>{formatRate(type.defaultRatePercent)}</TableCell>
                <TableCell>{type.withholdingUsageCount}</TableCell>
                <TableCell>
                  <Badge variant={type.isActive ? 'success' : 'neutral'}>
                    {type.isActive ? 'Activo' : 'Inactivo'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={Pencil}
                      aria-label={`Editar ${type.name}`}
                      onClick={() => openModal({ mode: 'edit', type })}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={
                        toggleActiveMutation.isPending &&
                        toggleActiveMutation.variables?.id === type.id
                      }
                      onClick={() =>
                        toggleActiveMutation.mutate({ id: type.id, isActive: !type.isActive })
                      }
                    >
                      {type.isActive ? 'Desactivar' : 'Activar'}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      {modal !== null ? (
        <Modal
          open
          onClose={() => setModal(null)}
          title={modal.mode === 'create' ? 'Nuevo tipo de retención' : 'Editar tipo de retención'}
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            {saveMutation.isError ? (
              <Alert tone="danger">{mapWithholdingTypeError(saveMutation.error)}</Alert>
            ) : null}

            <Field
              label="Nombre"
              name="name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />

            <Field
              label="Jurisdicción"
              name="jurisdiction"
              value={jurisdiction}
              onChange={(event) => setJurisdiction(event.target.value)}
            />

            <Field
              label="Alícuota por defecto (%)"
              name="defaultRatePercent"
              inputMode="decimal"
              hint="Opcional. Solo sugiere el importe en la cobranza."
              {...(parsedRate.valid ? {} : { error: RATE_ERROR })}
              value={rate}
              onChange={(event) => setRate(event.target.value)}
            />

            <div className="flex items-center gap-3">
              <Button
                loading={saveMutation.isPending}
                disabled={name.trim() === '' || !parsedRate.valid}
                onClick={() => saveMutation.mutate()}
              >
                Confirmar
              </Button>
              <Button variant="secondary" onClick={() => setModal(null)}>
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
